<template>
  <v-list class="pa-2 rounded-lg">
    <template v-if="usersStore.isLoading || tenantsStore.isLoading">
      <v-list-item>
        <v-list-item-title class="d-flex justify-center align-center pa-4">
          <v-progress-circular indeterminate color="primary" size="24" />
        </v-list-item-title>
      </v-list-item>
    </template>
    <template v-else>
      <div class="d-flex justify-space-between align-center px-4 py-2">
        <span class="text-h6 font-weight-regular">Gebruikers</span>
        <v-btn
          prepend-icon="mdi-account-plus"
          variant="tonal"
          color="primary"
          density="comfortable"
          size="small"
          @click="addUser"
        >
          Toevoegen
        </v-btn>
      </div>
      <v-divider class="mb-2"></v-divider>

      <!-- Error message -->
      <v-alert
        v-if="usersStore.hasError || tenantsStore.hasError"
        type="error"
        variant="tonal"
        class="mx-4 mb-2"
      >
        {{ usersStore.error || tenantsStore.error }}
      </v-alert>

      <v-container class="pa-0">
        <UserListItem
          v-for="user in users"
          :key="user.id"
          :user="user"
          class="mb-1"
        />
      </v-container>

      <!-- Cache indicator -->
      <div v-if="!usersStore.isLoading && cacheInfo" class="text-center py-2">
        <v-chip
          size="x-small"
          variant="text"
          color="success"
          prepend-icon="mdi-cached"
        >
          {{ cacheInfo }}
        </v-chip>
      </div>
    </template>
  </v-list>
</template>

<script>
import { onMounted, computed } from "vue";
import UserListItem from "./UserListItem.vue";
import { useFormsStore } from "@/stores/formsStore";
import { useUsersStoreRefactor } from "@/stores/usersStoreRefactor";
import { useTenantsStoreRefactor } from "@/stores/tenantsStoreRefactor";

export default {
  name: "UserListRefactor",
  components: {
    UserListItem,
  },

  setup() {
    const formsStore = useFormsStore();
    const usersStore = useUsersStoreRefactor();
    const tenantsStore = useTenantsStoreRefactor();

    // Computed properties
    const users = computed(() => usersStore.users);

    const cacheInfo = computed(() => {
      if (!usersStore.lastFetched) return null;

      const now = Date.now();
      const timeDiff = now - usersStore.lastFetched;
      const minutesAgo = Math.floor(timeDiff / (1000 * 60));

      if (minutesAgo < 1) {
        return "Data cached less than a minute ago";
      } else if (minutesAgo < 5) {
        return `Data cached ${minutesAgo} minute${minutesAgo > 1 ? "s" : ""} ago`;
      }

      return null;
    });

    // Methods
    const addUser = () => {
      formsStore.edit({
        type: "user",
        item: {
          first_name: "",
          last_name: "",
          email: "",
          roles: "",
          password: "",
          repeat_password: "",
          tenant_ids: [], // Initialize tenant_ids for new users
        },
      });
    };

    const loadData = async () => {
      try {
        // Load both users and tenants in parallel
        await Promise.all([usersStore.fetchAll(), tenantsStore.fetchAll()]);
        console.log("UserListRefactor: Loaded users and tenants from stores");
      } catch (error) {
        console.error("UserListRefactor: Error loading data:", error);
      }
    };

    // Lifecycle
    onMounted(async () => {
      // Initialize stores (sets up ActionCable if configured)
      await Promise.all([usersStore.init(), tenantsStore.init()]);

      // Load data
      await loadData();
    });

    return {
      usersStore,
      tenantsStore,
      users,
      cacheInfo,
      addUser,
    };
  },
};
</script>

<style scoped>
.v-chip {
  opacity: 0.7;
}
</style>
